import React, { useState } from "react";
import { API_BASE } from "@/lib/api";
import { Download, FileText, Eye, Loader2, X } from "lucide-react";

// Readable text on the brand colour: dark ink on light/orange brands, white on dark ones (WCAG AA).
export const onColor = (hex) => { try { const h = (hex || "#003E87").replace("#", ""); const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16); const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255; const L = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255; return L > 0.5 ? "#111827" : "#ffffff"; } catch { return "#ffffff"; } };
const kb = (n) => (n ? `${Math.max(1, Math.round(n / 1024))} KB` : "");
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

// L2: the file is fetched and saved under its filed name; the address bar never moves off the Power Ray page.
function serverName(r) {
  const m = /filename="([^"]+)"/.exec(r.headers.get("content-disposition") || "");
  return m ? m[1] : null;
}

async function saveAs(url, filename) {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) {
    let msg = "This file is not available right now. Please contact Power Ray.";
    try { const j = await r.json(); msg = j?.detail?.message || j?.detail || msg; } catch { /* keep message */ }
    throw new Error(msg);
  }
  const blob = await r.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename || serverName(r) || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

const q = (preview, viewer, extra) => { const p = new URLSearchParams(extra || {}); if (preview) p.set("preview", "1"); if (viewer) p.set("viewer", viewer); const t = p.toString(); return t ? `?${t}` : ""; };

export function DownloadButton({ token, d, primary, preview, viewer }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const go = async () => {
    setBusy(true); setErr(null);
    try { await saveAs(`${API_BASE}/public/portal/${token}/doc/${d.item_id}${q(preview, viewer)}`, d.filename); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <span className="inline-flex flex-col items-end gap-1">
    {err && <span className="text-[11px] text-rose-700" data-testid={`portal-doc-error-${d.item_id}`}>{err}</span>}
    <button onClick={go} disabled={busy} className="inline-flex items-center gap-1.5 text-sm font-medium rounded-md px-3 py-1.5 shrink-0 disabled:opacity-60" style={{ background: primary, color: onColor(primary) }} data-testid={`portal-doc-download-${d.item_id}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download · {d.file_type || "File"}{d.size ? ` · ${kb(d.size)}` : ""}
    </button>
    </span>
  );
}

export function DownloadAllButton({ token, primary, count, preview, viewer }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!count) return null;
  const go = async () => {
    setBusy(true); setErr(null);
    try { await saveAs(`${API_BASE}/public/portal/${token}/zip${q(preview, viewer)}`, null); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <span className="inline-flex flex-col items-end gap-1">
    {err && <span className="text-[11px] text-rose-700" data-testid="portal-download-all-error">{err}</span>}
    <button onClick={go} disabled={busy} className="inline-flex items-center gap-1.5 text-sm font-medium rounded-md px-3 py-1.5 border" style={{ borderColor: primary, color: primary }} data-testid="portal-download-all">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download all ({count}) · Zip
    </button>
    </span>
  );
}

// L3: a PDF is read inside the branded page (same address); Excel is never previewed.
export function PdfViewer({ token, d, onClose, preview, viewer }) {
  const [src, setSrc] = useState(null);
  React.useEffect(() => {
    let url;
    fetch(`${API_BASE}/public/portal/${token}/doc/${d.item_id}${q(preview, viewer, { inline: "1" })}`, { credentials: "include" })
      .then((r) => r.blob()).then((b) => { url = URL.createObjectURL(new Blob([b], { type: "application/pdf" })); setSrc(url); }).catch(() => setSrc("error"));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [token, d.item_id, preview, viewer]);
  return (
    <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden" data-testid={`portal-pdf-viewer-${d.item_id}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 text-xs text-slate-600">
        <span className="truncate">{d.filename}</span>
        <button onClick={onClose} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800" data-testid={`portal-pdf-close-${d.item_id}`}><X className="w-3.5 h-3.5" /> Close</button>
      </div>
      {src === "error" ? <div className="p-4 text-sm text-slate-500">This file could not be shown here. Use Download.</div>
        : src ? <iframe title={d.filename} src={src} className="w-full" style={{ height: "70vh" }} />
        : <div className="p-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
    </div>
  );
}

export function DocRow({ token, d, primary, preview, viewer }) {
  const [open, setOpen] = useState(false);
  const isPdf = (d.file_type || "") === "PDF";
  return (
    <li className="py-2.5" data-testid={`portal-doc-${d.item_id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-slate-800 break-words">{d.label}{d.filename ? <span className="text-slate-400"> · {d.filename}</span> : null}</div>
          <div className="text-[11px] text-slate-400">{[d.round_label, fmtDay(d.released_at), d.file_type, kb(d.size)].filter(Boolean).join(" · ")}</div>
        </div>
        {isPdf && (
          <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-sm underline shrink-0" style={{ color: primary }} data-testid={`portal-doc-read-${d.item_id}`}>
            <Eye className="w-4 h-4" /> {open ? "Hide" : "Read here"}
          </button>
        )}
        <DownloadButton token={token} d={d} primary={primary} preview={preview} viewer={viewer} />
      </div>
      {open && <PdfViewer token={token} d={d} onClose={() => setOpen(false)} preview={preview} viewer={viewer} />}
    </li>
  );
}
